import { observer } from "mobx-react-lite";
import ShopHistoryTable from "../../pages/shop/components/ShopHistoryTable";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ActionsSection, ButtonsSection } from "e.sic-platform.webclient/src/components/data-components/sections/DataSections";
import AddNewButton from "e.sic-platform.webclient/src/components/buttons/add-new-button/AddNewButton";
import LoadButton from "../buttons/app-action-buttons/LoadButton";
import RedoButton from "../buttons/app-action-buttons/RedoButton";
import { translationService } from "e.sic-platform.webclient/src/services/translation/TranslationService";
import { Dialog } from "e.sic-platform.webclient/src/components/dialog/Dialog";
import CopyUrlButton from "../buttons/copy-url-button/CopyUrlButton";
import { AppStorage } from "e.sic-platform.webclient/src/services/app/AppStorage";
import WarningBeforeAction from "e.sic-platform.webclient/src/components/data-components/WarningBeforeAction";
import OrderOverview from "./OrderOverview";
import { creditBalanceStorage } from "e.sic-platform.webclient/src/services/shop/CreditBalanceStorage";
import { authentication } from "e.sic-platform.webclient/src/services/authentication/Authentication";
import { Box, Checkbox, FormControlLabel, Stack } from "@mui/material";
import { ActivityTypes } from "e.sic-platform.webclient/src/services/shop/ActivityPricesStorage";
import { useSmartAppContext } from "./SmartAppContext";

export interface IHotbarProps {
    appStorage: AppStorage
    actionBar?: any;
    // Suppress the built-in Redo button + dialog (used by Inspection Insights, which
    // provides its own multi-service redo dialog covering OCR/Analysis/Segmentation).
    hideRedo?: boolean;
}

export default observer(function SmartAppHotbar(props: IHotbarProps) {
    const {appStorage} = {...props};
    const { stickyActionBar } = useSmartAppContext();
    const [showLoading, setShowLoading] = useState(false);
    const [showRedo, setShowRedo] = useState(false);
    // `hasEnoughBalance`: true if the user can afford the selected redo activities.
    // (The previous `redoEnabled` variable inverted this value — fixed.)
    const [hasEnoughBalance, setHasEnoughBalance] = useState(true);

    const toggleRedo = useCallback(() => {
        setShowRedo(!showRedo);
    }, [showRedo])

    const toggleLoading = useCallback(async () => {
        await appStorage.historyStorage.load();
        setShowLoading(!showLoading)
    }, [showLoading, appStorage.historyStorage]);

    // Re-validate every time the user toggles a redo checkbox so the warning + button
    // state reflect the *selected* services, not the full app activity type.
    const redoActivitiesKey = appStorage.redoSelection
        ? [...appStorage.redoSelection].sort().join(",")
        : "";
    useEffect(() => {
        const activities = appStorage.redoSelection?.length > 0
            ? appStorage.redoSelection
            : appStorage.activityType;
        creditBalanceStorage.validateRemainingBalance(activities).then((result) => {
            setHasEnoughBalance(!!result);
        });
    }, [redoActivitiesKey, appStorage.activityType, showRedo]);

    const redoWarningKey = useMemo(() => {
        if (authentication.anonymous) return "shop.authentication-needed";
        if (!hasEnoughBalance) return "shop.balance-exceeded";
        return "";
    }, [hasEnoughBalance]);

    const redoButtonDisabled =
        !hasEnoughBalance ||
        (appStorage.redoSelection?.length ?? 0) === 0;

    useEffect(() => {
        setShowLoading(false)
    }, [appStorage.fileGuid])
    
    return (
        <>
            <Dialog titleKey="general.apps.load.title" isVisible={showLoading} onHide={toggleLoading} maxWidth="lg">
                <ShopHistoryTable rowClickPath={appStorage.historyStorage.clickPath} shopHistoryStorage={appStorage.historyStorage} showDeleteButton={false} noSelection={true} />
            </Dialog>
            <WarningBeforeAction
                titleKey="general.redo.confirm.title"
                isVisible={showRedo}
                toggleModal={toggleRedo}
                optionalWarningKey={redoWarningKey}
                buttonOk={{
                    textKey: "general.redo",
                    action: () => {
                        appStorage.redo();
                        toggleRedo();
                    },
                    disabled: redoButtonDisabled
                }}
            >
                <div>{translationService.translate("general.redo.confirm.description")}</div>
                <Stack pr={8}>
                    {appStorage.redoableServices.map((service: {label: string, service: ActivityTypes}) => (
                        <FormControlLabel 
                            control={<Checkbox checked={appStorage.redoSelection.includes(service.service)} onChange={() => appStorage.toggleRedoSelection(service.service)}/>} 
                            label={translationService.translate(service.label)} 
                        />
                    ))}
                    <OrderOverview price={appStorage.redoPrice} />
                </Stack>
            </WarningBeforeAction>
            <Box
                sx={stickyActionBar
                    ? {
                        position: "sticky",
                        top: "50px",
                        zIndex: 9,
                        width: "100%",
                        maxWidth: "100%",
                        boxSizing: "border-box",
                        backgroundColor: "#fff",
                        pl: 2,
                        pr: 2,
                        "& .actions-container": {
                          paddingBottom: "0 !important",
                          maxWidth: "100%",
                        },
                        "& .actions-header": {
                          border: "none !important",
                          minHeight: "36px",
                          flexWrap: "wrap",
                          "& > *": {
                            padding: "2px 8px !important",
                          },
                        },
                      }
                    : { overflow: "scroll", scrollbarWidth: "none" }
                }
            >
                <ActionsSection isSticky={false}>
                    <ButtonsSection>
                        <AddNewButton onClick={appStorage.new} buttonText={translationService.translate("general.new")} />
                        <LoadButton onClick={toggleLoading} />
                        {!props.hideRedo && <RedoButton onClick={toggleRedo} disabled={appStorage.step < 2}/>}
                        {props.actionBar}
                    </ButtonsSection>
                    <ButtonsSection>
                        <CopyUrlButton id={appStorage.fileId} disabled={!appStorage.fileId}/>
                    </ButtonsSection>
                </ActionsSection>
            </Box>
        </>
    )
})